import { API_URL, authenticatedFetch, ApiError, parseApiErrorMessage } from "../config/api";

/** The document lifecycle/access event this log entry records. */
export type DocumentAuditAction = 'download' | 'created' | 'updated' | 'deleted';

/**
 * One document audit-trail entry — covers downloads plus create/update/delete
 * lifecycle events (see `action`). Timestamps are already converted
 * server-side to the project's local timezone — `timezone` names which IANA
 * zone `created_at` is expressed in. Do not reinterpret `created_at` in the
 * browser's local zone.
 */
export interface DocumentDownloadLog {
  id: string;
  document_id: string;
  document_name: string;
  content_type: string;
  file_type_label: string;
  file_size: number | null;
  action: DocumentAuditAction;
  entity_table: string | null;
  entity_id: string | null;
  /** Null for non-project documents. */
  project_id: string | null;
  project_name: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
  timezone: string;
}

export interface DocumentDownloadLogListResponse {
  total: number;
  page: number;
  size: number;
  results: DocumentDownloadLog[];
}

export interface DocumentDownloadLogFilters {
  user_id?: string;
  document_id?: string;
  action?: DocumentAuditAction;
  document_name?: string;
  content_type?: string;
  file_size_min?: number;
  file_size_max?: number;
  date_from?: string; // YYYY-MM-DD, interpreted server-side in the project's timezone
  date_to?: string;
}

export type DownloadLogExportFormat = 'csv' | 'pdf';

/** Returned instead of a file when the result set is large enough to be queued asynchronously. */
export interface DownloadLogExportQueuedResponse {
  status: 'queued';
  job_id: string;
  estimated_rows: number;
  message: string;
}

export type DownloadLogExportResult =
  | { blob: Blob; headers: Headers }
  | DownloadLogExportQueuedResponse;

const appendFilters = (params: URLSearchParams, filters: DocumentDownloadLogFilters): URLSearchParams => {
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.document_id) params.append('document_id', filters.document_id);
  if (filters.action) params.append('action', filters.action);
  if (filters.document_name) params.append('document_name', filters.document_name);
  if (filters.content_type) params.append('content_type', filters.content_type);
  if (filters.file_size_min !== undefined) params.append('file_size_min', filters.file_size_min.toString());
  if (filters.file_size_max !== undefined) params.append('file_size_max', filters.file_size_max.toString());
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);

  return params;
};

const buildListParams = (filters: DocumentDownloadLogFilters, page: number, size: number): URLSearchParams =>
  appendFilters(new URLSearchParams({ page: page.toString(), size: size.toString() }), filters);

const buildExportParams = (filters: DocumentDownloadLogFilters, format: DownloadLogExportFormat): URLSearchParams =>
  appendFilters(new URLSearchParams({ format }), filters);

const fetchLogsList = async (
  basePath: string,
  filters: DocumentDownloadLogFilters,
  page: number,
  size: number,
): Promise<DocumentDownloadLogListResponse> => {
  const params = buildListParams(filters, page, size);
  const r = await authenticatedFetch(`${API_URL}${basePath}?${params.toString()}`);

  if (!r.ok) {
    const message = await parseApiErrorMessage(r, `Failed to load download logs (${r.status})`);
    throw new ApiError(r.status, r.statusText, undefined, message);
  }

  return r.json();
};

/**
 * Streams the export as a file for small result sets; large ones are queued
 * and delivered by email instead — callers must check which shape came back
 * (see `DownloadLogExportResult`).
 */
const fetchExport = async (
  basePath: string,
  format: DownloadLogExportFormat,
  filters: DocumentDownloadLogFilters,
): Promise<DownloadLogExportResult> => {
  const params = buildExportParams(filters, format);
  const r = await authenticatedFetch(`${API_URL}${basePath}?${params.toString()}`);

  if (!r.ok) {
    const message = await parseApiErrorMessage(r, `Failed to export download log (${r.status})`);
    throw new ApiError(r.status, r.statusText, undefined, message);
  }

  if (r.status === 202) {
    return (await r.json()) as DownloadLogExportQueuedResponse;
  }

  return { blob: await r.blob(), headers: r.headers };
};

// ============================================
// Project-scoped
// ============================================

/** List a project's document download log, most-recent-first. */
export const getProjectDownloadLogs = (
  projectId: string,
  filters: DocumentDownloadLogFilters = {},
  page = 1,
  size = 100,
): Promise<DocumentDownloadLogListResponse> =>
  fetchLogsList(`/projects/${projectId}/admin/documents/download-logs`, filters, page, size);

/** List the download history for a single document within a project. */
export const getDocumentDownloadLogs = (
  projectId: string,
  documentId: string,
  filters: DocumentDownloadLogFilters = {},
  page = 1,
  size = 100,
): Promise<DocumentDownloadLogListResponse> =>
  fetchLogsList(`/projects/${projectId}/admin/documents/${documentId}/download-logs`, filters, page, size);

/** Export a project's download log as CSV or PDF. */
export const exportProjectDownloadLogs = (
  projectId: string,
  format: DownloadLogExportFormat,
  filters: DocumentDownloadLogFilters = {},
): Promise<DownloadLogExportResult> =>
  fetchExport(`/projects/${projectId}/admin/documents/download-logs/export`, format, filters);

// ============================================
// Global (system-admin only)
// ============================================

/** List the download log across every project. System-admin only, enforced server-side. */
export const getGlobalDownloadLogs = (
  filters: DocumentDownloadLogFilters = {},
  page = 1,
  size = 100,
): Promise<DocumentDownloadLogListResponse> =>
  fetchLogsList('/documents/admin/download-logs', filters, page, size);

/** Export the global (all-projects) download log as CSV or PDF. */
export const exportGlobalDownloadLogs = (
  format: DownloadLogExportFormat,
  filters: DocumentDownloadLogFilters = {},
): Promise<DownloadLogExportResult> =>
  fetchExport('/documents/admin/download-logs/export', format, filters);
